'use client';

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  LYRRA_A11Y_STORAGE_KEY,
  LYRRA_A11Y_UPDATED_EVENT,
} from '@/hooks/useKeyboardNavigationSetting';
import {
  isMacPlatform,
  isReadingMaskDecreaseKey,
  isReadingMaskIncreaseKey,
  isReadingMaskModifier,
  readingMaskModifierLabel,
} from '@/lib/a11y-keyboard-shortcuts';

const STORAGE_KEY = 'lyrra:reading-mask:bandHeight';
const DEFAULT_BAND_HEIGHT = 96;
const MIN_BAND_HEIGHT = 32;
const MAX_BAND_HEIGHT = 220;
const VEIL_OPACITY = 0.78;
const Z_INDEX = 9700;

function readReadingMaskEnabled(): boolean {
  try {
    const raw = localStorage.getItem(LYRRA_A11Y_STORAGE_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as { readingMask?: boolean };
    return Boolean(parsed.readingMask);
  } catch {
    return false;
  }
}

function readStoredBandHeight(): number {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_BAND_HEIGHT;
    const parsed = Number.parseInt(raw, 10);
    if (!Number.isFinite(parsed)) return DEFAULT_BAND_HEIGHT;
    return Math.min(MAX_BAND_HEIGHT, Math.max(MIN_BAND_HEIGHT, parsed));
  } catch {
    return DEFAULT_BAND_HEIGHT;
  }
}

function persistBandHeight(value: number): void {
  try {
    localStorage.setItem(STORAGE_KEY, String(value));
  } catch {
    /* ignore */
  }
}

/**
 * Masque de lecture : deux voiles opaques laissent une bande claire centrée sur
 * le curseur, pour isoler la ligne en cours (utile dyslexie / TDA-H).
 * Mises à jour en RAF + styles inline → pas de re-render à chaque mouvement.
 */
const ReadingMaskInner: React.FC = () => {
  const [enabled, setEnabled] = useState(readReadingMaskEnabled);
  const [bandHeight, setBandHeight] = useState<number>(readStoredBandHeight);

  const topRef = useRef<HTMLDivElement | null>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const bandRef = useRef<HTMLDivElement | null>(null);
  const hintRef = useRef<HTMLDivElement | null>(null);

  const yRef = useRef<number>(typeof window !== 'undefined' ? window.innerHeight / 2 : 0);
  const targetYRef = useRef<number>(yRef.current);
  const bandHeightRef = useRef<number>(bandHeight);
  const rafRef = useRef<number>(0);
  const hintTimerRef = useRef<number | null>(null);

  useEffect(() => {
    bandHeightRef.current = bandHeight;
  }, [bandHeight]);

  useEffect(() => {
    const sync = () => setEnabled(readReadingMaskEnabled());
    window.addEventListener(LYRRA_A11Y_UPDATED_EVENT, sync);
    return () => window.removeEventListener(LYRRA_A11Y_UPDATED_EVENT, sync);
  }, []);

  const showHint = useCallback((message: string) => {
    const hint = hintRef.current;
    if (!hint) return;
    hint.textContent = message;
    hint.style.opacity = '1';
    if (hintTimerRef.current) window.clearTimeout(hintTimerRef.current);
    hintTimerRef.current = window.setTimeout(() => {
      if (hintRef.current) hintRef.current.style.opacity = '0';
    }, 1200);
  }, []);

  useEffect(() => {
    if (!enabled) return;

    const initialY = window.innerHeight / 2;
    yRef.current = initialY;
    targetYRef.current = initialY;

    const onPointerMove = (e: PointerEvent) => {
      targetYRef.current = e.clientY;
    };
    const onTouchMove = (e: TouchEvent) => {
      const touch = e.touches[0];
      if (touch) targetYRef.current = touch.clientY;
    };
    const onWheel = (e: WheelEvent) => {
      const next = Math.min(
        window.innerHeight - 4,
        Math.max(4, targetYRef.current + e.deltaY * 0.25),
      );
      targetYRef.current = next;
    };
    const onKey = (e: KeyboardEvent) => {
      const ae = document.activeElement as HTMLElement | null;
      const tag = ae?.tagName ?? '';
      if (
        ae?.isContentEditable ||
        tag === 'INPUT' ||
        tag === 'TEXTAREA' ||
        tag === 'SELECT'
      ) {
        return;
      }
      if (!isReadingMaskModifier(e)) return;

      if (isReadingMaskIncreaseKey(e)) {
        const next = Math.min(MAX_BAND_HEIGHT, bandHeightRef.current + 12);
        bandHeightRef.current = next;
        setBandHeight(next);
        persistBandHeight(next);
        showHint(`Bande : ${next}px`);
        e.preventDefault();
        e.stopPropagation();
      } else if (isReadingMaskDecreaseKey(e)) {
        const next = Math.max(MIN_BAND_HEIGHT, bandHeightRef.current - 12);
        bandHeightRef.current = next;
        setBandHeight(next);
        persistBandHeight(next);
        showHint(`Bande : ${next}px`);
        e.preventDefault();
        e.stopPropagation();
      }
    };

    window.addEventListener('pointermove', onPointerMove, { passive: true });
    window.addEventListener('touchmove', onTouchMove, { passive: true });
    window.addEventListener('wheel', onWheel, { passive: true });
    window.addEventListener('keydown', onKey, { capture: true });

    const tick = () => {
      const target = targetYRef.current;
      yRef.current += (target - yRef.current) * 0.22;
      const y = yRef.current;
      const h = bandHeightRef.current;
      const top = topRef.current;
      const bottom = bottomRef.current;
      const band = bandRef.current;
      const half = h / 2;
      if (top) top.style.height = `${Math.max(0, y - half)}px`;
      if (bottom) {
        const bottomTop = y + half;
        bottom.style.top = `${bottomTop}px`;
        bottom.style.height = `${Math.max(0, window.innerHeight - bottomTop)}px`;
      }
      if (band) {
        band.style.top = `${y - half}px`;
        band.style.height = `${h}px`;
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);

    const mod = readingMaskModifierLabel();
  showHint(
      isMacPlatform()
        ? `Masque activé — ${mod} + touche =/+ ou ${mod} + ↑ pour élargir, ${mod} + − ou ${mod} + ↓ pour réduire`
        : `Masque activé — ${mod} + + / ${mod} + − pour ajuster la bande`,
    );

    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('wheel', onWheel);
      window.removeEventListener('keydown', onKey, { capture: true });
      cancelAnimationFrame(rafRef.current);
      if (hintTimerRef.current) {
        window.clearTimeout(hintTimerRef.current);
        hintTimerRef.current = null;
      }
    };
  }, [enabled, showHint]);

  if (!enabled || typeof document === 'undefined') return null;

  return createPortal(
    <div className="pointer-events-none" aria-hidden="true">
      <div
        ref={topRef}
        style={{
          position: 'fixed',
          left: 0,
          right: 0,
          top: 0,
          height: 0,
          background: `rgba(8, 12, 22, ${VEIL_OPACITY})`,
          backdropFilter: 'blur(1px)',
          WebkitBackdropFilter: 'blur(1px)',
          zIndex: Z_INDEX,
          transition: 'background-color 120ms ease',
        }}
      />
      <div
        ref={bottomRef}
        style={{
          position: 'fixed',
          left: 0,
          right: 0,
          top: 0,
          height: 0,
          background: `rgba(8, 12, 22, ${VEIL_OPACITY})`,
          backdropFilter: 'blur(1px)',
          WebkitBackdropFilter: 'blur(1px)',
          zIndex: Z_INDEX,
        }}
      />
      <div
        ref={bandRef}
        style={{
          position: 'fixed',
          left: 0,
          right: 0,
          top: 0,
          height: bandHeight,
          zIndex: Z_INDEX + 1,
          boxShadow:
            'inset 0 1px 0 rgba(255,255,255,0.18), inset 0 -1px 0 rgba(255,255,255,0.18), 0 0 0 1px rgba(255, 196, 0, 0.55)',
          background:
            'linear-gradient(to bottom, rgba(255, 196, 0, 0.0) 0%, rgba(255, 196, 0, 0.06) 50%, rgba(255, 196, 0, 0.0) 100%)',
        }}
      />
      <div
        ref={hintRef}
        role="status"
        aria-live="polite"
        style={{
          position: 'fixed',
          left: '50%',
          bottom: 24,
          transform: 'translateX(-50%)',
          padding: '8px 14px',
          borderRadius: 999,
          background: 'rgba(0,0,0,0.78)',
          color: '#fff',
          fontSize: 13,
          fontWeight: 500,
          letterSpacing: 0.2,
          opacity: 0,
          transition: 'opacity 220ms ease',
          zIndex: Z_INDEX + 2,
          pointerEvents: 'none',
          boxShadow: '0 10px 28px rgba(0,0,0,0.35)',
        }}
      />
    </div>,
    document.body,
  );
};

export const ReadingMask = React.memo(ReadingMaskInner);
export default ReadingMask;
